import React, { useState, useEffect, useContext } from 'react';
import SEOHead from './SEOHead';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Sparkles, Eye, Users, Plus, Edit, Trash2, MoreVertical, CheckCircle, XCircle, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import Footer from './Footer';

const API = process.env.REACT_APP_BACKEND_URL;

const OrganizerDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [metrics, setMetrics] = useState({ active_events: 0, total_views: 0, total_registrations: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [showRegs, setShowRegs] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const isAdmin = user?.email === 'shifracs@gmail.com' || user?.email === 'Advisor@mednerd.in' || user?.role === 'admin' || user?.role === 'subadmin';
    if (!user || !isAdmin) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchEvents();
    // eslint-disable-next-line
  }, []);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/events/admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEvents(res.data.events || []);
      setMetrics(res.data.metrics || {});
    } catch (e) {
      toast.error('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrations = async (event) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/events/${event.id}/registrations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRegistrations(res.data.registrations || []);
      setSelectedEvent(event);
      setShowRegs(true);
    } catch (e) {
      toast.error('Failed to fetch registrations');
    }
  };

  const deleteEvent = async (id) => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/events/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Event deleted');
      fetchEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
    setDeleting(false);
    setConfirmDeleteId(null);
  };

  const toggleStatus = async (ev) => {
    const newStatus = ev.status === 'active' ? 'closed' : 'active';
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/events/${ev.id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Event ${newStatus}`);
      fetchEvents();
    } catch (e) {
      toast.error('Status update failed');
    }
  };

  const statusColor = (s) => ({
    active: 'bg-green-100 text-green-700 border-green-200',
    draft: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    closed: 'bg-gray-100 text-gray-700 border-gray-200',
  }[s] || 'bg-gray-100 text-gray-700');

  if (showRegs && selectedEvent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <Button variant="ghost" onClick={() => setShowRegs(false)} className="mb-2">← Back to Dashboard</Button>
          <h1 className="text-2xl font-bold text-gray-900">Registrations — {selectedEvent.title}</h1>
          <p className="text-gray-600 mb-4">{registrations.length} registrant(s)</p>
          {registrations.length === 0 ? (
            <Card><CardContent className="py-12 text-center"><Users className="h-12 w-12 text-gray-300 mx-auto mb-3" /><p className="text-gray-700">No registrations yet</p></CardContent></Card>
          ) : (
            <Card><CardContent className="p-0">
              {/* Desktop */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="p-3 font-medium text-gray-500">Name</th>
                      <th className="p-3 font-medium text-gray-500">Email</th>
                      <th className="p-3 font-medium text-gray-500">Mobile</th>
                      <th className="p-3 font-medium text-gray-500">College</th>
                      <th className="p-3 font-medium text-gray-500">Payment</th>
                      <th className="p-3 font-medium text-gray-500">Registered</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrations.map((r) => (
                      <tr key={r.id} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium text-gray-900">{r.user_name}</td>
                        <td className="p-3 text-gray-600">{r.user_email}</td>
                        <td className="p-3 text-gray-600">{r.user_mobile || '-'}</td>
                        <td className="p-3 text-gray-600">{r.user_college || '-'}</td>
                        <td className="p-3">
                          {r.payment_status === 'paid' ? (
                            <Badge className="bg-blue-100 text-blue-700 border-blue-200">₹{r.amount_paid?.toLocaleString('en-IN')}</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700 border-green-200">Free</Badge>
                          )}
                        </td>
                        <td className="p-3 text-gray-500 text-xs">{new Date(r.registered_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Mobile */}
              <div className="md:hidden divide-y divide-gray-100">
                {registrations.map((r) => (
                  <div key={r.id} className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 text-sm truncate">{r.user_name}</div>
                        <div className="text-xs text-gray-500 truncate">{r.user_email}</div>
                        {r.user_mobile && <div className="text-xs text-gray-500 mt-0.5">{r.user_mobile}</div>}
                        {r.user_college && <div className="text-xs text-gray-500 truncate">{r.user_college}</div>}
                      </div>
                      <div className="text-right shrink-0">
                        {r.payment_status === 'paid' ? (
                          <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px]">₹{r.amount_paid?.toLocaleString('en-IN')}</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px]">Free</Badge>
                        )}
                        <div className="text-[10px] text-gray-400 mt-1">{new Date(r.registered_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent></Card>
          )}
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
      <SEOHead title="Organizer Dashboard" description="Manage your published events on Nerd Nation." path="/organizer-dashboard" noIndex={true} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2"><Sparkles className="h-7 w-7 text-orange-500" />Organizer Dashboard</h1>
            <p className="text-sm text-gray-600 mt-1">Manage your events and registrations</p>
          </div>
          <Button onClick={() => navigate('/post-event')} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="post-event-btn">
            <Plus className="h-4 w-4 mr-2" /> Post New Event
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <Card><CardContent className="p-5 flex items-center gap-3"><div className="p-2 rounded-lg bg-green-100"><Calendar className="h-5 w-5 text-green-600" /></div><div><div className="text-xs text-gray-500">Active Events</div><div className="text-2xl font-bold">{metrics.active_events}</div></div></CardContent></Card>
          <Card><CardContent className="p-5 flex items-center gap-3"><div className="p-2 rounded-lg bg-blue-100"><Eye className="h-5 w-5 text-blue-600" /></div><div><div className="text-xs text-gray-500">Total Views</div><div className="text-2xl font-bold">{metrics.total_views}</div></div></CardContent></Card>
          <Card><CardContent className="p-5 flex items-center gap-3"><div className="p-2 rounded-lg bg-purple-100"><Users className="h-5 w-5 text-purple-600" /></div><div><div className="text-xs text-gray-500">Registrations</div><div className="text-2xl font-bold">{metrics.total_registrations}</div></div></CardContent></Card>
        </div>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="py-10 text-center text-gray-500">Loading…</div>
            ) : events.length === 0 ? (
              <div className="py-12 text-center">
                <Sparkles className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <p className="font-medium text-gray-900">No events yet</p>
                <p className="text-sm text-gray-500 mb-4">Get started by posting your first event</p>
                <Button onClick={() => navigate('/post-event')} className="bg-orange-500 hover:bg-orange-600 text-white">
                  <Plus className="h-4 w-4 mr-2" /> Post New Event
                </Button>
              </div>
            ) : (
              <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="p-3 font-medium text-gray-500">Title</th>
                      <th className="p-3 font-medium text-gray-500">Type</th>
                      <th className="p-3 font-medium text-gray-500">Status</th>
                      <th className="p-3 font-medium text-gray-500">Views</th>
                      <th className="p-3 font-medium text-gray-500">Registrations</th>
                      <th className="p-3 font-medium text-gray-500">Date</th>
                      <th className="p-3 font-medium text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((ev) => (
                      <tr key={ev.id} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium text-gray-900 max-w-xs truncate">{ev.title}</td>
                        <td className="p-3">
                          {ev.event_type === 'free' ? (
                            <Badge className="bg-green-100 text-green-700 border-green-200">Free</Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-700 border-blue-200">₹{ev.fee?.toLocaleString('en-IN')}</Badge>
                          )}
                        </td>
                        <td className="p-3"><Badge className={statusColor(ev.status)}>{ev.status}</Badge></td>
                        <td className="p-3 text-gray-600">{ev.views || 0}</td>
                        <td className="p-3">
                          <button onClick={() => fetchRegistrations(ev)} className="text-orange-600 hover:text-orange-700 font-medium">{ev.registrations_count || 0}</button>
                        </td>
                        <td className="p-3 text-gray-500 text-xs">{ev.event_date ? new Date(ev.event_date).toLocaleDateString() : '-'}</td>
                        <td className="p-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => fetchRegistrations(ev)}><Users className="h-4 w-4 mr-2" /> View Registrations</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/post-event?edit=${ev.id}`)}><Edit className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => toggleStatus(ev)}>
                                {ev.status === 'active' ? (<><XCircle className="h-4 w-4 mr-2" /> Close</>) : (<><CheckCircle className="h-4 w-4 mr-2" /> Activate</>)}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setConfirmDeleteId(ev.id)} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-gray-100">
                {events.map((ev) => (
                  <div key={ev.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 text-sm leading-snug line-clamp-2">{ev.title}</div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                          {ev.event_type === 'free' ? (
                            <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px] h-5">Free</Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px] h-5">₹{ev.fee?.toLocaleString('en-IN')}</Badge>
                          )}
                          <Badge className={`${statusColor(ev.status)} text-[10px] h-5`}>{ev.status}</Badge>
                          {ev.event_date && <span className="text-[11px] text-gray-500">{new Date(ev.event_date).toLocaleDateString()}</span>}
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-600">
                          <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{ev.views || 0} views</span>
                          <button onClick={() => fetchRegistrations(ev)} className="flex items-center gap-1 text-orange-600 font-medium">
                            <Users className="h-3 w-3" />{ev.registrations_count || 0} registered
                          </button>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="-mt-1 -mr-1"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => fetchRegistrations(ev)}><Users className="h-4 w-4 mr-2" /> View Registrations</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/post-event?edit=${ev.id}`)}><Edit className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleStatus(ev)}>
                            {ev.status === 'active' ? (<><XCircle className="h-4 w-4 mr-2" /> Close</>) : (<><CheckCircle className="h-4 w-4 mr-2" /> Activate</>)}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setConfirmDeleteId(ev.id)} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
      <Footer />

      {confirmDeleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <h3 className="font-bold text-gray-900 text-lg mb-1">Delete Event?</h3>
            <p className="text-gray-500 text-sm mb-5">This action cannot be undone. All registrations for this event will also be removed.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDeleteId(null)} className="flex-1 py-2 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50">Cancel</button>
              <button onClick={() => deleteEvent(confirmDeleteId)} disabled={deleting} className="flex-1 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-60">{deleting ? 'Deleting…' : 'Yes, Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizerDashboard;
